import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { formatOdds, oddsToProbabilityPercent } from '../../utils';
import type { Fight, FightResult } from '../../types';

/**
 * Format fight result with full details
 */
function formatFightResult(result: FightResult, winnerName: string): string {
  // Method labels
  const methodLabels: Record<FightResult['method'], string> = {
    ko_tko: 'KO/TKO',
    submission: 'Submission',
    decision_unanimous: 'Unanimous Decision',
    decision_majority: 'Majority Decision',
    decision_split: 'Split Decision',
    dq: 'Disqualification',
    draw: 'Draw',
    no_contest: 'No Contest',
  };

  const method = methodLabels[result.method] || result.method;
  
  // Handle draw and no contest
  if (result.method === 'draw') {
    return `Fight ended in a Draw`;
  }
  if (result.method === 'no_contest') {
    return `Fight ended with No Contest`;
  }

  // Base result text
  let resultText = `${winnerName} won by ${method}`;

  // Add round and time details if available
  if (!result.wentTheDistance && result.round) {
    resultText += ` in Round ${result.round}`;
    if (result.timeInRound) {
      resultText += ` at ${result.timeInRound}`;
    }
  } else if (result.wentTheDistance) {
    resultText += ` (went the distance)`;
  }

  return resultText;
}

interface FightCardProps {
  fight: Fight;
  onPropsClick?: (fightId: string) => void;
  canBet: boolean;
}

/**
 * Fight Card Component
 * Displays fight information with fighters, odds, and status
 */
export function FightCard({ fight, onPropsClick, canBet }: FightCardProps) {
  const statusColors = {
    upcoming: 'info',
    'in-progress': 'warning',
    live: 'error',
    completed: 'default',
  } as const;

  const statusLabels = {
    upcoming: 'Upcoming',
    'in-progress': 'In Progress',
    live: 'Live',
    completed: 'Completed',
  };

  return (
    <Card padding="md" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-primary-text">Fight</h3>
        <Badge variant={statusColors[fight.status]} size="sm">
          {statusLabels[fight.status]}
        </Badge>
      </div>

      <div className="space-y-3">
        {fight.fighters.map((fighter) => {
          const hasOddsMovement = fighter.previousOdds !== undefined && fighter.previousOdds !== fighter.odds;
          const oddsIncreased = fighter.previousOdds !== undefined && fighter.odds > fighter.previousOdds;
          
          return (
            <div
              key={fighter.id}
              className="flex items-center justify-between p-3 bg-tertiary-bg rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium text-primary-text">{fighter.name}</div>
                <div className="text-sm text-secondary-text">
                  {oddsToProbabilityPercent(fighter.odds).toFixed(1)}% chance
                </div>
                {hasOddsMovement && (
                  <div className={`text-xs mt-1 ${oddsIncreased ? 'text-error-red' : 'text-success-green'}`}>
                    {oddsIncreased ? '↑' : '↓'} {formatOdds(fighter.previousOdds || 0)} → {formatOdds(fighter.odds)}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold text-primary-text text-lg">
                  {formatOdds(fighter.odds)}
                </div>
                <div className="text-xs text-secondary-text">American</div>
              </div>
            </div>
          );
        })}
      </div>

      {fight.status === 'upcoming' && canBet && fight.markets && fight.markets.length > 0 && onPropsClick && (
        <Button
          variant="primary"
          fullWidth
          onClick={() => onPropsClick(fight.id)}
        >
          View All Bets
        </Button>
      )}

      {fight.status === 'live' && (
        <div className="text-center text-sm text-warning">
          Fight in progress - betting closed
        </div>
      )}

      {fight.status === 'completed' && fight.result && (
        <div className="pt-3 border-t border-border">
          <div className="text-sm text-secondary-text">Result:</div>
          <div className="font-medium text-primary-text mt-1">
            {formatFightResult(
              fight.result,
              fight.fighters.find((f) => f.id === fight.result?.winnerId)?.name || 'Unknown'
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

